import { useState, useEffect, useRef } from 'react';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { API_URL } from '../../../config/api';
import EmailShipment from '../EmailShipment/EmailShipment';

// PDF Styles for return packing slip (green theme)
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    textAlign: 'center',
    marginBottom: 20,
    borderBottom: '2px solid #27ae60',
    paddingBottom: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#27ae60',
  },
  shipmentNumber: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    borderBottom: '1px solid #bdc3c7',
    paddingBottom: 4,
  },
  infoGrid: {
    marginBottom: 10,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  infoLabel: {
    width: '30%',
    fontWeight: 'bold',
  },
  infoValue: {
    width: '70%',
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#27ae60',
    color: 'white',
    padding: 6,
    fontWeight: 'bold',
    fontSize: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #bdc3c7',
    padding: 5,
    fontSize: 9,
  },
  tableRowEven: {
    backgroundColor: '#e8f8f5',
  },
  tableFooter: {
    flexDirection: 'row',
    backgroundColor: '#e8f8f5',
    padding: 6,
    fontWeight: 'bold',
    borderTop: '2px solid #27ae60',
  },
  colNumber: { width: '6%' },
  colBarcode: { width: '18%' },
  colOrderNo: { width: '12%' },
  colCustomer: { width: '22%' },
  colWigType: { width: '22%' },
  colWeight: { width: '10%', textAlign: 'right' },
  footer: {
    marginTop: 30,
    paddingTop: 15,
    borderTop: '1px solid #bdc3c7',
  },
  signatureSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  signatureLine: {
    width: '45%',
  },
  signatureText: {
    borderTop: '1px solid #2c3e50',
    paddingTop: 5,
    fontSize: 10,
  },
});

// PDF Document Component
const ReturnPackingSlipPDF = ({ data }) => (
  <Document>
    <Page size="LETTER" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>RETURN SHIPMENT RECEIPT</Text>
        <Text style={styles.shipmentNumber}>Return #{data.shipmentDetails.shipmentId}</Text>
      </View>

      {/* Return Information */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Return Shipment Information</Text>
        <View style={styles.infoGrid}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Received At:</Text>
            <Text style={styles.infoValue}>{data.shipmentDetails.ShippingToName}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Return Date:</Text>
            <Text style={styles.infoValue}>
              {data.shipmentDetails.ShipReturnDate
                ? new Date(data.shipmentDetails.ShipReturnDate).toLocaleDateString()
                : 'N/A'}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Courier:</Text>
            <Text style={styles.infoValue}>{data.shipmentDetails.CourrierName || 'N/A'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Tracking #:</Text>
            <Text style={styles.infoValue}>{data.shipmentDetails.TrackingNumber || 'N/A'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Entered By:</Text>
            <Text style={styles.infoValue}>{data.shipmentDetails.EnteredBy || 'N/A'}</Text>
          </View>
          {data.shipmentDetails.SpecialInstructions && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Notes:</Text>
              <Text style={styles.infoValue}>{data.shipmentDetails.SpecialInstructions}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Returned Items Table */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Items Received ({data.wigs.length})</Text>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={styles.colNumber}>#</Text>
            <Text style={styles.colBarcode}>Barcode</Text>
            <Text style={styles.colOrderNo}>Order #</Text>
            <Text style={styles.colCustomer}>Customer</Text>
            <Text style={styles.colWigType}>Wig Type</Text>
            <Text style={styles.colWeight}>Weight (g)</Text>
          </View>

          {/* Table Rows */}
          {data.wigs.map((wig, index) => (
            <View
              key={wig.barcode || index}
              style={[styles.tableRow, index % 2 === 1 ? styles.tableRowEven : {}]}
            >
              <Text style={styles.colNumber}>{wig.Number}</Text>
              <Text style={styles.colBarcode}>{wig.barcode}</Text>
              <Text style={styles.colOrderNo}>{wig.OrderNo}</Text>
              <Text style={styles.colCustomer}>{wig.customerName || '-'}</Text>
              <Text style={styles.colWigType}>{wig.wigType || '-'}</Text>
              <Text style={styles.colWeight}>
                {wig.wigWeight ? parseFloat(wig.wigWeight).toFixed(3) : '0.000'}
              </Text>
            </View>
          ))}

          {/* Table Footer */}
          <View style={styles.tableFooter}>
            <Text style={{ width: '90%', textAlign: 'right' }}>Total Weight:</Text>
            <Text style={styles.colWeight}>{data.totalWeight} g</Text>
          </View>
        </View>
      </View>

      {/* Signature Section */}
      <View style={styles.footer}>
        <View style={styles.signatureSection}>
          <View style={styles.signatureLine}>
            <Text style={styles.signatureText}>Received By: _____________________</Text>
          </View>
          <View style={styles.signatureLine}>
            <Text style={styles.signatureText}>Date: _____________________</Text>
          </View>
        </View>
      </View>
    </Page>
  </Document>
);

// Main Component
const ReturnShippingPrintView = ({ shipmentId, onClose }) => {
  const [shipmentData, setShipmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printTriggered, setPrintTriggered] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const hasOpenedRef = useRef(false);
  const iframeRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => {
    if (shipmentId && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      fetchShipmentData();
    }

    // Cleanup function
    return () => {
      if (iframeRef.current) {
        try {
          document.body.removeChild(iframeRef.current);
        } catch (e) {
          console.error('Error removing iframe:', e);
        }
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
    };
  }, [shipmentId]);

  const fetchShipmentData = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/api/rachels-room/return-shipping-print/${shipmentId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch return shipment data');
      }

      const data = await response.json();
      setShipmentData(data);
      setLoading(false);

      // Automatically open PDF
      await openPDF(data);
    } catch (err) {
      console.error('Error fetching return shipment data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const openPDF = async (data) => {
    try {
      const doc = <ReturnPackingSlipPDF data={data} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);

      // Store URL for cleanup
      urlRef.current = url;

      // Create hidden iframe to trigger print dialog
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);

      // Store iframe reference for cleanup
      iframeRef.current = iframe;

      // Wait for iframe to load, then trigger print
      iframe.onload = function() {
        setTimeout(() => {
          try {
            setPrintTriggered(true);
            iframe.contentWindow.print();
          } catch (printError) {
            console.error('Error triggering print:', printError);
            // Fallback: open in new window if iframe print fails
            window.open(url, '_blank');
            if (onClose) {
              onClose();
            }
          }
        }, 500);
      };

      // Handle iframe load errors
      iframe.onerror = function(error) {
        console.error('Error loading PDF in iframe:', error);
        setError('Failed to load PDF');
        URL.revokeObjectURL(url);
        if (onClose) {
          onClose();
        }
      };
    } catch (error) {
      console.error('Error opening PDF:', error);
      setError(`Failed to generate PDF: ${error.message}`);
      if (onClose) {
        onClose();
      }
    }
  };

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>Error: {error}</p>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              padding: '12px 30px',
              backgroundColor: '#95a5a6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              marginTop: '20px',
            }}
          >
            Close
          </button>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Preparing return receipt for printing...</p>
      </div>
    );
  }

  const handleEmailClick = (e) => {
    e.stopPropagation();
    setShowEmailModal(true);
  };

  return (
    <>
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          userSelect: 'none'
        }}
      >
        <p style={{ marginBottom: '15px', fontSize: '16px', color: '#27ae60' }}>
          {printTriggered ? 'Print dialog opened' : 'Processing...'}
        </p>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
          <button
            onClick={handleEmailClick}
            style={{
              padding: '12px 24px',
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#219a52'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#27ae60'}
          >
            Email Return Receipt
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
          >
            Close
          </button>
        </div>

        <p style={{ fontSize: '13px', color: '#888', marginTop: '20px' }}>
          Click "Close" to dismiss or "Email" to send the return receipt
        </p>
      </div>

      {/* Email Modal */}
      <EmailShipment
        isOpen={showEmailModal}
        shipmentId={shipmentId}
        shipmentDetails={shipmentData?.shipmentDetails}
        wigs={shipmentData?.wigs || []}
        wigCount={shipmentData?.wigs?.length || 0}
        onClose={() => setShowEmailModal(false)}
        onSuccess={(result) => {
          console.log('Email sent successfully:', result);
        }}
        onError={(err) => {
          console.error('Email failed:', err);
        }}
        isReturn={true}
      />
    </>
  );
};

export default ReturnShippingPrintView;
